import React from 'react';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  return (
    <>
      {/* Page Header */}
      <section className="bg-dark text-white py-5 mt-5">
        <div className="container py-4">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">About Us</h1>
              <p className="lead mb-0">Meet the team behind Sustainability Pros — MEP building-assessment specialists and the makers of MEP Tools.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80"
                alt="Sustainability Pros consulting team"
                className="img-fluid rounded-4 shadow-lg"
              />
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <h2 className="mb-4">Our <span className="text-primary">Story</span></h2>
              <p className="lead mb-4">Founded in 2023, Sustainability Pros was built on a simple conviction: building owners deserve better tools and better advocacy when it comes to how their facilities are designed, built, and maintained.</p>
              <p className="mb-4">Our founders bring hands-on MEP field experience — from duct leakage testing and airflow balancing to whole-building condition and energy assessments — combined with a drive to modernize how that work gets documented, delivered, and acted on.</p>
              <p className="mb-0">We work as an extension of the owner's team, helping them cut energy use and operating costs and keep their facilities performing over time. And through our MEP Tools software platform, we're building the field tools that MEP professionals and facility managers actually need — in the cloud, on mobile, with no manual re-entry.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission and Values Section */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Our Mission & Values</h2>
              <p className="lead">The principles that guide our work and define our approach to sustainable construction.</p>
            </div>
          </div>
          
          <div className="row mb-5">
            <div className="col-lg-10 mx-auto">
              <div className="card p-4 p-md-5" data-aos="fade-up">
                <h3 className="mb-4 text-primary">Our Mission</h3>
                <p className="lead mb-4">To help every building run more efficiently and sustainably — and to give the people who manage those buildings the data, tools, and advocacy they need to cut costs and keep systems performing.</p>
                <div className="bg-primary bg-opacity-10 p-3 rounded-3 border-start border-4 border-primary">
                  <p className="mb-0">
                    <strong>Our Approach:</strong> We follow the <Link to="/services" className="text-primary">"Highway to Sustainability"</Link> — a structured 5-phase methodology covering everything from Owner Project Requirements through post-occupancy operations. Our MEP Tools platform brings that same rigor to the field, letting technicians and facility managers document, calculate, and report from any device.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6 col-lg-3" data-aos="fade-up">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-person-check-fill"></i>
                </div>
                <h4>Owner Advocacy</h4>
                <p className="mb-0">We always work for the owner — assessing systems honestly, flagging what's underperforming, and making sure your capital goes where it delivers the most value.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-tools"></i>
                </div>
                <h4>Field Expertise</h4>
                <p className="mb-0">Our team has hands-on experience with duct leakage testing, airflow balancing, boiler assessment, and building automation systems.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-lightbulb"></i>
                </div>
                <h4>Built-for-Purpose Software</h4>
                <p className="mb-0">MEP Tools was built by practitioners for practitioners — cloud-native, mobile-first, and designed around the actual workflows of MEP field work.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-clipboard2-check-fill"></i>
                </div>
                <h4>Thorough Documentation</h4>
                <p className="mb-0">From facility assessment reports to energy-savings analyses, we produce clear, actionable documentation that owners can actually use.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Meet Our <span className="text-primary">Founders</span></h2>
              <p className="lead">Three practitioners who got tired of doing MEP assessment work with inadequate tools — so they built better ones.</p>
            </div>
          </div>

          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4" data-aos="fade-up">
              <div className="card h-100 text-center border-0 shadow-sm">
                <div className="pt-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center mx-auto" style={{ width: '100px', height: '100px', fontSize: '2rem', fontWeight: 700 }}>
                    CF
                  </div>
                </div>
                <div className="card-body">
                  <h4>Chad Foley</h4>
                  <p className="text-primary mb-3">Co-Founder</p>
                  <p className="mb-0 text-muted fst-italic">Bio coming soon.</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 text-center border-0 shadow-sm">
                <div className="pt-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center mx-auto" style={{ width: '100px', height: '100px', fontSize: '2rem', fontWeight: 700 }}>
                    GJ
                  </div>
                </div>
                <div className="card-body">
                  <h4>Gil Johnson</h4>
                  <p className="text-primary mb-3">Co-Founder</p>
                  <p className="mb-0 text-muted fst-italic">Bio coming soon.</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 text-center border-0 shadow-sm">
                <div className="pt-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center mx-auto" style={{ width: '100px', height: '100px', fontSize: '2rem', fontWeight: 700 }}>
                    PC
                  </div>
                </div>
                <div className="card-body">
                  <h4>Paul Curd</h4>
                  <p className="text-primary mb-3">Co-Founder</p>
                  <p className="mb-0 text-muted fst-italic">Bio coming soon.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary text-white text-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="mb-4">Ready to work with a team that's been in the field?</h2>
              <p className="lead mb-4">Whether you want an existing building assessment, an energy and sustainability analysis, or access to MEP Tools, we'd like to hear about your project.</p>
              <Link to="/contact" className="btn btn-light btn-lg">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;